import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useError } from '../../contexts/ErrorContext';
import ReceivablesLayout from './ReceivablesLayout';
import PlatformButton from '../PlatformButton';
import PlatformInput from '../PlatformInput';
import PlatformText from '../PlatformText';
import { useReceivables, RECEIVABLE_ERRORS } from './hooks/useReceivables';
import { useSalespeople } from './hooks/useSalespeople';
import { useSimplifiedSecurity } from '../../hooks/useSimplifiedSecurity';
import { useTranslation } from '../../hooks/useTranslation';
import {
  normalizePartyNameForDisplay,
  normalizePartyNameForStorage
} from '../../utils/platformUtils';
import {
  addDays,
  cleanCustomerName,
  formatDate,
  formatMoney,
  parseDateInput,
  toDate,
  toDateInputValue
} from './receivablesUtils';

const MODES = {
  ORDER: 'order',
  MANUAL: 'manual'
};

const PunchSalePage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { t } = useTranslation('receivables');
  const { organizationId } = useOrganization();
  const { showErrorToast } = useError();
  const { canManageReceivables } = useSimplifiedSecurity();
  const { createSale, updateSale, getReceivable, loadEligibleOrders, removeSale } = useReceivables('none');
  const salespeople = useSalespeople();

  const [mode, setMode] = useState(MODES.ORDER);
  const [orderSearch, setOrderSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderLookupError, setOrderLookupError] = useState('');
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showOrderOptions, setShowOrderOptions] = useState(false);

  const [customerName, setCustomerName] = useState('');
  const [amount, setAmount] = useState('');
  const [saleDate, setSaleDate] = useState(toDateInputValue(new Date()));
  const [creditDays, setCreditDays] = useState('');
  const [salespersonId, setSalespersonId] = useState('');
  const [extraSalesperson, setExtraSalesperson] = useState(null);
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const salespersonOptions = useMemo(() => {
    const list = [...salespeople];
    if (extraSalesperson && !list.some((person) => person.id === extraSalesperson.id)) {
      list.unshift(extraSalesperson);
    }
    return list;
  }, [salespeople, extraSalesperson]);

  const dueDatePreview = useMemo(() => {
    const parsed = parseDateInput(saleDate);
    const days = Number(creditDays);
    if (!parsed || creditDays === '' || isNaN(days) || days < 0) return null;
    return addDays(parsed, days);
  }, [saleDate, creditDays]);

  const resolveSalesperson = useCallback(
    async (preferredId) => {
      if (!preferredId) {
        setSalespersonId('');
        return;
      }

      const matched = salespeople.find((person) => person.id === preferredId);
      if (matched) {
        setExtraSalesperson(null);
        setSalespersonId(matched.id);
        return;
      }

      if (!organizationId) {
        setSalespersonId(preferredId);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, `organizations/${organizationId}/users/${preferredId}`));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setExtraSalesperson({
            id: preferredId,
            fullName: data.fullName || data.email || preferredId,
            email: data.email,
            role: data.role
          });
        }
      } catch (lookupError) {
        setExtraSalesperson({ id: preferredId, fullName: preferredId });
      }
      setSalespersonId(preferredId);
    },
    [organizationId, salespeople]
  );

  useEffect(() => {
    if (!isEdit) return undefined;

    let active = true;
    getReceivable(id).then((sale) => {
      if (!active) return;
      if (!sale) {
        setFormError(t('errors.saveFailed'));
        return;
      }
      setMode(sale.source === 'order' ? MODES.ORDER : MODES.MANUAL);
      setCustomerName(normalizePartyNameForDisplay(sale.customerName) || sale.customerName || '');
      setAmount(String(sale.invoiceAmount ?? ''));
      setSaleDate(toDateInputValue(sale.saleDate) || toDateInputValue(new Date()));
      setCreditDays(sale.creditDays === 0 || sale.creditDays ? String(sale.creditDays) : '');
      setNote(sale.note || '');
      resolveSalesperson(sale.salespersonId);
      if (sale.orderNumber) {
        setSelectedOrder({
          id: sale.orderId,
          orderNumber: sale.orderNumber,
          partyName: sale.customerName,
          orderValue: sale.invoiceAmount
        });
      }
    });

    return () => {
      active = false;
    };
    // t/resolveSalesperson are new functions every render (or when salespeople
    // load) and would reload the sale, wiping credit days while the user types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, getReceivable]);

  const applyOrder = useCallback(
    (order) => {
      setSelectedOrder(order);
      setCustomerName(normalizePartyNameForDisplay(order.partyName) || order.partyName || '');
      setAmount(order.orderValue ? String(order.orderValue) : '');
      const dispatchDate = toDate(order.dispatchedAt);
      setSaleDate(toDateInputValue(dispatchDate || new Date()));
      resolveSalesperson(order.assignedTo || order.createdBy);
      setOrderSearch(order.orderNumber != null ? String(order.orderNumber) : '');
      setShowOrderOptions(false);
      setOrderLookupError('');
      setFormError('');
    },
    [resolveSalesperson]
  );

  const lookupErrorMessage = useCallback((lookupError) => {
    const message = lookupError?.message;
    if (message === RECEIVABLE_ERRORS.ORDER_ALREADY_LINKED) return t('sale.orderAlreadyLinked');
    if (message === RECEIVABLE_ERRORS.ORDER_NOT_ELIGIBLE) return t('sale.orderNotEligible');
    if (message === RECEIVABLE_ERRORS.PERMISSION_DENIED) return t('errors.permissionDenied');
    return t('sale.orderNotFound');
  }, [t]);

  const saveErrorMessage = useCallback((saveError) => {
    const message = saveError?.message;
    if (message === RECEIVABLE_ERRORS.ORDER_ALREADY_LINKED) return t('sale.orderAlreadyLinked');
    if (message === RECEIVABLE_ERRORS.AMOUNT_BELOW_RECEIVED) return t('errors.amountBelowReceived');
    if (message === RECEIVABLE_ERRORS.PERMISSION_DENIED) return t('errors.permissionDenied');
    return t('errors.saveFailed');
  }, [t]);

  const filteredOrderOptions = useMemo(() => {
    const term = orderSearch.replace(/^#/, '').trim().toLowerCase();
    if (!term) return eligibleOrders.slice(0, 30);
    return eligibleOrders
      .filter((order) => {
        const numberText = String(order.orderNumber ?? '').toLowerCase();
        const nameText = String(order.partyName || '').toLowerCase();
        return numberText.includes(term) || nameText.includes(term);
      })
      .slice(0, 30);
  }, [eligibleOrders, orderSearch]);

  useEffect(() => {
    if (isEdit || mode !== MODES.ORDER) return undefined;

    let active = true;
    setLoadingOrders(true);

    loadEligibleOrders()
      .then((matches) => {
        if (!active) return;
        setEligibleOrders(matches);
        setOrderLookupError(matches.length === 0 ? t('sale.noEligibleOrders') : '');
      })
      .catch((lookupError) => {
        if (!active) return;
        setEligibleOrders([]);
        setOrderLookupError(lookupErrorMessage(lookupError));
      })
      .finally(() => {
        if (active) setLoadingOrders(false);
      });

    return () => {
      active = false;
    };
    // t/lookupErrorMessage are new functions every render and would retrigger
    // this load forever, leaving the dropdown stuck on "Searching".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, mode, loadEligibleOrders]);

  const handleOrderSearchChange = useCallback((event) => {
    setOrderSearch(event.target.value);
    setShowOrderOptions(true);
    setOrderLookupError('');
  }, []);

  const handleSubmit = useCallback(async () => {
    const numericAmount = Number(amount);
    const numericDays = Number(creditDays);
    const parsedSaleDate = parseDateInput(saleDate);
    const cleanedName = cleanCustomerName(customerName);

    if (!isEdit && mode === MODES.ORDER && !selectedOrder) {
      setFormError(t('errors.orderRequired'));
      return;
    }
    if (!cleanedName) {
      setFormError(t('errors.customerRequired'));
      return;
    }
    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      setFormError(t('errors.amountPositive'));
      return;
    }
    if (!parsedSaleDate) {
      setFormError(t('errors.saleDateRequired'));
      return;
    }
    if (creditDays === '' || isNaN(numericDays) || numericDays < 0) {
      setFormError(t('errors.creditDaysInvalid'));
      return;
    }
    if (salespeople.length > 0 && !salespersonId) {
      setFormError(t('errors.salespersonRequired'));
      return;
    }

    setSubmitting(true);
    setFormError('');

    const payload = {
      customerName: normalizePartyNameForStorage(cleanedName) || cleanedName,
      invoiceAmount: numericAmount,
      saleDate: parsedSaleDate,
      creditDays: numericDays,
      salespersonId,
      note: note.trim(),
      orderId: !isEdit && mode === MODES.ORDER ? selectedOrder.id : undefined,
      orderNumber: !isEdit && mode === MODES.ORDER ? selectedOrder.orderNumber ?? null : undefined
    };

    try {
      if (isEdit) {
        await updateSale(id, payload);
        showErrorToast(t('messages.saleUpdated'), { variant: 'info', autoHide: true, duration: 3000 });
      } else {
        await createSale(payload);
        showErrorToast(t('messages.saleRecorded'), { variant: 'info', autoHide: true, duration: 3000 });
      }
      navigate('/receivables');
    } catch (submitError) {
      const message = saveErrorMessage(submitError);
      setFormError(message);
      showErrorToast(message, { variant: 'error', autoHide: true, duration: 4000 });
    } finally {
      setSubmitting(false);
    }
  }, [
    amount,
    creditDays,
    saleDate,
    customerName,
    isEdit,
    mode,
    selectedOrder,
    salespersonId,
    note,
    salespeople.length,
    updateSale,
    id,
    createSale,
    showErrorToast,
    saveErrorMessage,
    navigate,
    t
  ]);

  const handleDelete = useCallback(async () => {
    if (!isEdit) return;
    setSubmitting(true);
    try {
      await removeSale(id);
      showErrorToast(t('messages.saleDeleted'), { variant: 'info', autoHide: true, duration: 3000 });
      navigate('/receivables');
    } catch (deleteError) {
      setFormError(t('errors.saveFailed'));
      setSubmitting(false);
    }
  }, [isEdit, id, removeSale, showErrorToast, navigate, t]);

  if (!canManageReceivables) {
    return (
      <ReceivablesLayout title={t('sale.title')} subtitle={t('sale.subtitle')} backTo="/receivables">
        <div className="receivables-empty">
          <div className="receivables-empty-icon">🔒</div>
          <PlatformText variant="body">{t('errors.accessDenied')}</PlatformText>
        </div>
      </ReceivablesLayout>
    );
  }

  return (
    <ReceivablesLayout
      title={isEdit ? t('sale.editTitle') : t('sale.title')}
      subtitle={isEdit ? t('sale.editSubtitle') : t('sale.subtitle')}
      backTo="/receivables"
    >
      <div className="receivables-form-card">
      <div className="receivables-form">
        {formError && <div className="receivables-notice">{formError}</div>}

        {!isEdit && (
          <div className="receivables-tabs">
            <button
              type="button"
              className={`receivables-tab${mode === MODES.ORDER ? ' active' : ''}`}
              onClick={() => {
                setMode(MODES.ORDER);
                setFormError('');
              }}
            >
              {t('sale.fromOrder')}
            </button>
            <button
              type="button"
              className={`receivables-tab${mode === MODES.MANUAL ? ' active' : ''}`}
              onClick={() => {
                setMode(MODES.MANUAL);
                setSelectedOrder(null);
                setFormError('');
              }}
            >
              {t('sale.manual')}
            </button>
          </div>
        )}

        {!isEdit && mode === MODES.ORDER && (
          <div className="receivables-field receivables-autocomplete">
            <label htmlFor="order-search">{t('sale.searchOrder')}</label>
            {selectedOrder ? (
              <div className="receivables-selected">
                <span>
                  {t('sale.linkedOrder', { orderNumber: selectedOrder.orderNumber })}
                  {selectedOrder.partyName ? ` · ${normalizePartyNameForDisplay(selectedOrder.partyName) || selectedOrder.partyName}` : ''}
                  {selectedOrder.orderValue ? ` · ${formatMoney(selectedOrder.orderValue)}` : ''}
                </span>
                <button
                  type="button"
                  className="receivables-link-button"
                  onClick={() => {
                    setSelectedOrder(null);
                    setCustomerName('');
                    setAmount('');
                    setSalespersonId('');
                    setOrderSearch('');
                    setShowOrderOptions(true);
                  }}
                >
                  {t('actions.cancel')}
                </button>
              </div>
            ) : (
              <>
                <div className="receivables-autocomplete-wrap">
                  <PlatformInput
                    id="order-search"
                    type="text"
                    inputMode="search"
                    autoComplete="off"
                    value={orderSearch}
                    onChange={handleOrderSearchChange}
                    onFocus={() => setShowOrderOptions(true)}
                    onBlur={() => {
                      window.setTimeout(() => setShowOrderOptions(false), 150);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        if (filteredOrderOptions[0]) {
                          applyOrder(filteredOrderOptions[0]);
                        }
                      }
                    }}
                    placeholder={t('sale.searchOrderPlaceholder')}
                  />
                  {showOrderOptions && (
                    <ul className="receivables-autocomplete-options" role="listbox">
                      {loadingOrders && eligibleOrders.length === 0 ? (
                        <li className="receivables-autocomplete-empty">{t('sale.searching')}</li>
                      ) : filteredOrderOptions.length === 0 ? (
                        <li className="receivables-autocomplete-empty">
                          {orderSearch.trim() ? t('sale.noMatchingOrders') : t('sale.noEligibleOrders')}
                        </li>
                      ) : (
                        filteredOrderOptions.map((order) => (
                          <li key={order.id}>
                            <button
                              type="button"
                              className="receivables-autocomplete-option"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => applyOrder(order)}
                            >
                              <span className="receivables-autocomplete-title">
                                #{order.orderNumber} · {normalizePartyNameForDisplay(order.partyName) || order.partyName || t('fields.customer')}
                              </span>
                              <span className="receivables-autocomplete-meta">
                                {order.status === 'DISPATCHED' ? t('sale.dispatched') : t('sale.readyToDispatch')}
                                {order.orderValue ? ` · ${formatMoney(order.orderValue)}` : ''}
                              </span>
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
              </>
            )}
            {orderLookupError && <div className="receivables-notice">{orderLookupError}</div>}
            <span className="receivables-field-hint">{t('sale.eligibleHint')}</span>
          </div>
        )}

        {(isEdit || mode === MODES.MANUAL || selectedOrder) && (
          <>
            {isEdit && selectedOrder?.orderNumber && (
              <div className="receivables-preview">
                <div className="receivables-preview-row">
                  <span>{t('fields.orderNumber')}</span>
                  <strong>#{selectedOrder.orderNumber}</strong>
                </div>
              </div>
            )}

            <div className="receivables-field">
              <label htmlFor="customer-name">{t('fields.customer')}</label>
              <PlatformInput
                id="customer-name"
                type="text"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
              />
            </div>

            <div className="receivables-field">
              <label htmlFor="sale-amount">{t('fields.invoiceAmount')}</label>
              <PlatformInput
                id="sale-amount"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>

            <div className="receivables-field-row">
              <div className="receivables-field">
                <label htmlFor="sale-date">{t('fields.saleDate')}</label>
                <PlatformInput
                  id="sale-date"
                  type="date"
                  value={saleDate}
                  onChange={(event) => setSaleDate(event.target.value)}
                />
                {!isEdit && mode === MODES.ORDER && !selectedOrder?.dispatchedAt && (
                  <span className="receivables-field-hint">{t('sale.dispatchDateHint')}</span>
                )}
              </div>
              <div className="receivables-field">
                <label htmlFor="credit-days">{t('fields.creditDays')}</label>
                <PlatformInput
                  id="credit-days"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  step="1"
                  value={creditDays}
                  onChange={(event) => setCreditDays(event.target.value)}
                />
              </div>
            </div>

            {dueDatePreview && (
              <div className="receivables-preview">
                <div className="receivables-preview-row">
                  <span>{t('fields.dueDate')}</span>
                  <strong>{formatDate(dueDatePreview)}</strong>
                </div>
              </div>
            )}

            <div className="receivables-field">
              <label htmlFor="sale-salesperson">{t('fields.salesperson')}</label>
              <select
                id="sale-salesperson"
                className="platform-select"
                value={salespersonId}
                onChange={(event) => setSalespersonId(event.target.value)}
              >
                <option value="">{t('fields.selectSalesperson')}</option>
                {salespersonOptions.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.fullName || person.email}
                  </option>
                ))}
              </select>
              {salespeople.length === 0 && (
                <span className="receivables-field-hint">{t('sale.noSalespeople')}</span>
              )}
            </div>

            <div className="receivables-field">
              <label htmlFor="sale-note">{t('fields.note')}</label>
              <textarea
                id="sale-note"
                className="input-field"
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={t('fields.notePlaceholder')}
              />
            </div>
          </>
        )}

        {formError && <div className="receivables-notice">{formError}</div>}

        <div className="receivables-form-actions">
          <PlatformButton type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? t('sale.submitting') : (isEdit ? t('sale.saveChanges') : t('sale.submit'))}
          </PlatformButton>
          <PlatformButton type="button" variant="outline" onClick={() => navigate('/receivables')} disabled={submitting}>
            {t('actions.cancel')}
          </PlatformButton>
        </div>

        {isEdit && (
          <div className="receivables-form-actions">
            {confirmDelete ? (
              <>
                <PlatformText variant="body">{t('sale.confirmDelete')}</PlatformText>
                <PlatformButton onClick={handleDelete} disabled={submitting}>
                  {t('actions.delete')}
                </PlatformButton>
                <PlatformButton variant="outline" onClick={() => setConfirmDelete(false)} disabled={submitting}>
                  {t('actions.cancel')}
                </PlatformButton>
              </>
            ) : (
              <button type="button" className="receivables-link-button receivables-danger" onClick={() => setConfirmDelete(true)}>
                {t('actions.delete')}
              </button>
            )}
          </div>
        )}
      </div>
      </div>
    </ReceivablesLayout>
  );
};

export default PunchSalePage;
